import { useState } from "react";
import { Helmet } from "react-helmet";
import { LuLoaderCircle, LuMenu } from "react-icons/lu";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { colors, typography } from "../../design/tokens";
import { CollectionType, DeviceType } from "../../model/rankings";
import { useSideMenuActions } from "../../view_stores/useSideMenuStore";
import { WelcomeAppBar } from "../welcome/components/WelcomeAppBar";
import { WelcomeFooter } from "../welcome/components/WelcomeFooter";
import { AppStoreRankingsDropdown } from "./components/AppStoreRankingsDropdown";
import { AppStoreRankingColumn } from "./components/AppStoreRankingColumn";
import { AppStoreRankingsSignInPrompt } from "./components/AppStoreRankingsSignInPrompt";
import { AppStoreRankingsStoreToggle } from "./components/AppStoreRankingsStoreToggle";
import { AppStoreRankingsSearch } from "./components/AppStoreRankingsSearch";
import { AppStoreRankingsSearchModal } from "./components/AppStoreRankingsSearchModal";
import { AppStoreRankingsSEOText } from "./components/AppStoreRankingsSEOText";
import { useAppStoreRankingsLogic } from "./hooks/useAppStoreRankingsLogic";

export function AppStoreRankingsPageCompact() {
  const isUserSignedIn = useIsUserSignedIn();
  const { open: openSideMenu } = useSideMenuActions();

  // Extract common business logic into reusable hook
  const {
    filters,
    setFilters,
    rankings,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    isSearchModalOpen,
    countryOptions,
    categoryOptions,
    deviceOptions,
    subcategoryOptions,
    searchPlaceholder,
    fetchRankings,
    handleStoreChange,
    handleCategoryChange,
    handleSearch,
    handleClearSearch,
    handleCloseSearchModal,
    isGamesCategory,
  } = useAppStoreRankingsLogic();

  // Mobile-specific state
  const [activeTab, setActiveTab] = useState<CollectionType>("free");

  const tabs: { key: CollectionType; label: string }[] = [
    { key: "free", label: "Free" },
    { key: "paid", label: "Paid" },
    { key: "grossing", label: "Grossing" },
  ];

  return (
    <>
      <Helmet>
        <title>Discover Apps - iOS & Android | Darvin</title>
        <meta
          name="description"
          content="Browse real-time app rankings from iOS App Store and Google Play. See top free, paid, and grossing apps across categories and countries."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        {/* SideMenu is globally available - hamburger triggers it on compact view */}

        {/* Header with gradient background */}
        <div className="flex flex-col relative bg-gradient-hero-dark">
          <div className="flex items-center relative z-10">
            {isUserSignedIn && (
              <button
                onClick={openSideMenu}
                className="p-4 text-white hover:opacity-80 transition-opacity"
              >
                <LuMenu size={24} />
              </button>
            )}
            <div className="flex-1">
              <WelcomeAppBar />
            </div>
          </div>
          <div className="px-4 pt-4 pb-12 text-center relative z-10">
            <h1
              className={`text-[32px] ${typography.weight.bold} text-white mb-2`}
            >
              Discover Apps
            </h1>
            <p className="text-base text-white/80">
              Search apps or browse top rankings across every category and country. Get inspired and build something similar.
            </p>
          </div>
          {/* Fade transition overlay */}
          <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none bg-gradient-to-b from-transparent via-dark-surface-primary/60 to-dark-surface-primary z-10" />
        </div>

        {/* Main content */}
        <div className={`flex-1 ${colors.surface.primary} px-4`}>
          <div className="pt-6 pb-8">
            {/* Store toggle */}
            <div className="flex justify-center mb-4">
              <AppStoreRankingsStoreToggle selected={filters.store} onChange={handleStoreChange} />
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-3 mb-4">
              <AppStoreRankingsDropdown
                options={countryOptions}
                value={filters.country}
                onChange={(v) => setFilters(prev => ({ ...prev, country: v }))}
              />
              {filters.store === "ios" && (
                <AppStoreRankingsDropdown
                  options={deviceOptions}
                  value={filters.device}
                  onChange={(v) => setFilters(prev => ({ ...prev, device: v as DeviceType }))}
                />
              )}
              <AppStoreRankingsDropdown
                options={categoryOptions}
                value={filters.category}
                onChange={handleCategoryChange}
              />
              {isGamesCategory(filters.category) && filters.store === "android" && (
                <AppStoreRankingsDropdown
                  options={subcategoryOptions}
                  value={filters.subcategory}
                  onChange={(v) => setFilters(prev => ({ ...prev, subcategory: v }))}
                />
              )}
              <AppStoreRankingsSearch
                value={searchQuery}
                onChange={setSearchQuery}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                isSearching={isSearching}
                placeholder={searchPlaceholder}
              />
            </div>

            {/* Content area */}
            <>
                {/* Tabs */}
                <div className="flex border-b border-dark-border mb-4">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex-1 py-3 text-center ${typography.size.sm} ${
                        typography.weight.medium
                      } transition-colors ${
                        activeTab === tab.key
                          ? "text-white border-b-2 border-primary-blue"
                          : `${colors.text.secondary} hover:text-white`
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Rankings content */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <LuLoaderCircle
                      className={`animate-spin ${colors.text.secondary}`}
                      size={32}
                    />
                  </div>
                ) : error ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className={`${colors.text.secondary} mb-4 text-center`}>
                      {error}
                    </p>
                    <button
                      onClick={fetchRankings}
                      className="px-4 py-2 bg-primary-blue text-white text-sm font-semibold tracking-wide rounded-full hover:opacity-90 transition-opacity"
                    >
                      Try Again
                    </button>
                  </div>
                ) : rankings ? (
                  <>
                    <AppStoreRankingColumn
                      apps={rankings[activeTab]}
                      store={filters.store}
                      country={filters.country}
                    />
                    {!isUserSignedIn && <AppStoreRankingsSignInPrompt />}
                  </>
                ) : null}
              </>
          </div>
        </div>

        <AppStoreRankingsSEOText />
        <WelcomeFooter />
      </div>

      {/* Search Results Modal */}
      <AppStoreRankingsSearchModal
        isOpen={isSearchModalOpen}
        onClose={handleCloseSearchModal}
        searchQuery={searchQuery}
        results={searchResults}
        rankings={rankings}
        store={filters.store}
        country={filters.country}
        isSearching={isSearching}
      />
    </>
  );
}
